import { Award, ArrowRight } from "lucide-react";
import LocalLink from "@/components/LocalLink";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFadeIn } from "@/hooks/useFadeIn";
import alexanderPortrait from "@/assets/alexander-portrait.webp";

const AboutSection = () => {
  const { t } = useLanguage();
  const { ref: sectionRef, isVisible } = useFadeIn();

  return (
    <section id="about" className="section-padding bg-secondary/40 scroll-mt-header">
      <div className="container-custom" ref={sectionRef}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side - Magazine Depth Effect */}
          <div className={`relative fade-up ${isVisible ? 'visible' : ''}`}>
            <div className="relative flex justify-center">
              {/* Navy backdrop rectangle offset behind image */}
              <div className="absolute top-4 -left-4 md:top-6 md:-left-6 w-full max-w-md h-full bg-navy rounded-xl" />
              
              {/* Main photo */}
              <img
                src={alexanderPortrait}
                alt="Alexander Arandia, Managing Attorney"
                className="relative z-10 w-full max-w-md h-auto max-h-[520px] object-cover object-top rounded-xl shadow-2xl"
                loading="lazy"
              />
              
              {/* Trust Badge - floating on top */}
              <div
                className={`absolute -bottom-6 -right-4 md:bottom-6 md:right-2 z-20 bg-card p-4 md:p-5 rounded-xl shadow-2xl border border-border/50 fade-up delay-300 ${isVisible ? 'visible' : ''}`}
              >
                <div className="flex flex-row items-center gap-3">
                  <div className="flex-shrink-0 bg-gold/10 p-2 rounded-lg">
                    <Award className="w-8 h-8 md:w-10 md:h-10 text-gold" />
                  </div>
                  <div className="text-left">
                    <span className="block text-base md:text-lg font-heading font-bold text-foreground leading-tight">{t("about.badge.title")}</span>
                    <span className="block text-xs md:text-sm font-sans text-muted-foreground">{t("about.badge.subtitle")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Side - Magazine Profile */}
          <div className={`fade-up delay-200 ${isVisible ? 'visible' : ''}`}>
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">
              {t("about.label")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground mt-3 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {t("about.title")}
            </h2>
            
            {/* Gold accent line */}
            <div className="w-24 h-1 bg-gold rounded-full mb-8" />
            
            <div className="space-y-4 text-muted-foreground leading-relaxed mb-8">
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph1") }} />
              <p dangerouslySetInnerHTML={{ __html: t("about.paragraph2") }} />
              <p>{t("about.paragraph3")}</p>
            </div>

            {/* Signature placeholder */}
            <div className="mb-8">
              <p className="italic text-foreground font-medium text-lg" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                — Alexander Arandia, Esq.
              </p>
              <div className="mt-2 w-48 h-px bg-border" />
            </div>

            {/* Stats with gold divider */}
            <div className={`flex items-center justify-center lg:justify-start gap-6 md:gap-10 fade-up delay-400 ${isVisible ? 'visible' : ''}`}>
              <div className="text-center">
                <span className="block text-3xl md:text-4xl font-heading font-bold text-gold-gradient">10,000+</span>
                <span className="text-xs md:text-sm text-muted-foreground">{t("about.stat.casesHandled")}</span>
              </div>
              
              {/* Gold vertical divider */}
              <div className="w-px h-14 bg-gold/50" />
              
              <div className="text-center">
                <span className="block text-3xl md:text-4xl font-heading font-bold text-gold-gradient">25+</span>
                <span className="text-xs md:text-sm text-muted-foreground">{t("about.stat.years")}</span>
              </div>
            </div>

            {/* Leva para a pagina do advogado, criada em 21/10. Ate aqui a home
                falava do Alexander sem ter para onde mandar quem quer saber mais. */}
            <LocalLink
              to="/about"
              className="mt-10 inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-gold px-7 py-3.5 font-semibold text-navy transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
            >
              {t("about.readProfile")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </LocalLink>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
